import { config } from "./config";

export interface JBALead {
  name: string;
  email: string;
  phone: string;
}

export interface JBACheckoutRecord {
  txRef: string;
  lead: JBALead;
  createdAt: string;
}

interface JBAApiResponseBase {
  success?: boolean;
  message?: string;
}

export interface JBACustomerPayload {
  name?: string;
  email: string;
  phone?: string;
}

export interface JBAFreePdfLeadRequest {
  source: string;
  session_id: string;
  customer: JBACustomerPayload;
}

export interface JBAFreePdfLeadResponse extends JBAApiResponseBase {}

export interface JBAInitializeCheckoutRequest {
  tx_ref: string;
  source: string;
  product_code: string;
  amount: number;
  currency: string;
  session_id: string;
  customer: JBACustomerPayload;
}

export interface JBAInitializeCheckoutResponse extends JBAApiResponseBase {
  txRef?: string;
  tx_ref?: string;
}

export interface JBAFulfillmentRequest {
  transaction_id: string;
  tx_ref: string;
  product_code: string;
  expected_amount: number;
  expected_currency: string;
  customer: JBALead | null;
}

export interface JBAFulfillmentResponse extends JBAApiResponseBase {
  redirectUrl?: string;
  whatsappUrl?: string;
  emailSent?: boolean;
}

const LEAD_STORAGE_KEY = "jba-lead";
const CHECKOUT_PREFIX = "jba-checkout:";
const SESSION_STORAGE_KEY = "jba-session-id";
const FREE_PDF_ENDPOINT = "/jba/leads/free-pdf";

const normalizeJBAString = (value: unknown, fallback = "") => {
  if (typeof value !== "string") {
    return fallback;
  }

  const normalizedValue = value.trim();
  return normalizedValue || fallback;
};

const normalizeJBAAmount = (value: unknown, fallback = 0) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const normalizedValue = Number(value.replace(/,/g, "").trim());

    if (Number.isFinite(normalizedValue)) {
      return normalizedValue;
    }
  }

  return fallback;
};

export const jbaConfig = {
  videoUrl: normalizeJBAString(config.JBA_VIDEO_URL),
  paystackPublicKey: normalizeJBAString(config.PAYSTACK_PUBLIC_KEY),
  flutterwavePublicKey: normalizeJBAString(config.FLUTTERWAVE_PUBLIC_KEY),
  amount: normalizeJBAAmount(config.JBA_PAYMENT_AMOUNT),
  currency: normalizeJBAString(config.JBA_PAYMENT_CURRENCY, "NGN"),
  title: normalizeJBAString(config.JBA_PAYMENT_TITLE, "JBA Launch Access"),
  description: normalizeJBAString(
    config.JBA_PAYMENT_DESCRIPTION,
    "JBA launch bundle with onboarding and community access."
  ),
  paymentOptions: normalizeJBAString(config.JBA_PAYMENT_OPTIONS, "card"),
  fulfillmentEndpoint: normalizeJBAString(config.JBA_FULFILLMENT_ENDPOINT),
  productCode: normalizeJBAString(config.JBA_PRODUCT_CODE, "jba"),
};

const createRandomIdPart = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID().replace(/-/g, "").slice(0, 16)
    : Math.random().toString(36).slice(2, 18);

const readStorage = <T>(key: string): T | null => {
  try {
    const value = localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    return;
  }
};

export const saveJBALead = (lead: JBALead) => {
  writeStorage(LEAD_STORAGE_KEY, lead);
};

export const loadJBALead = () => readStorage<JBALead>(LEAD_STORAGE_KEY);

export const clearJBALead = () => {
  try {
    localStorage.removeItem(LEAD_STORAGE_KEY);
  } catch {
    return;
  }
};

export const saveJBACheckoutRecord = (record: JBACheckoutRecord) => {
  writeStorage(`${CHECKOUT_PREFIX}${record.txRef}`, record);
};

export const loadJBACheckoutRecord = (txRef: string) =>
  readStorage<JBACheckoutRecord>(`${CHECKOUT_PREFIX}${txRef}`);

export const clearJBACheckoutRecord = (txRef: string) => {
  try {
    localStorage.removeItem(`${CHECKOUT_PREFIX}${txRef}`);
  } catch {
    return;
  }
};

export const getOrCreateJBASessionId = () => {
  const fallbackSessionId = `jba-session-${createRandomIdPart()}`;

  try {
    const existingSessionId = localStorage.getItem(SESSION_STORAGE_KEY);
    if (existingSessionId) {
      return existingSessionId;
    }

    localStorage.setItem(SESSION_STORAGE_KEY, fallbackSessionId);
    return fallbackSessionId;
  } catch {
    return fallbackSessionId;
  }
};

export const createJBATxRef = () => {
  const randomPart = createRandomIdPart();
  return `jba-${Date.now()}-${randomPart}`;
};

export const getJBASuccessUrl = () =>
  `${window.location.origin}/jba/payment/success`;

export const getJBAPaystackAmount = () => Math.round(jbaConfig.amount * 100);

export const getJBALogoUrl = () =>
  new URL("/logo.png", window.location.origin).toString();

const getJBAEndpointUrl = (endpoint: string) => {
  if (/^https?:\/\//i.test(endpoint)) {
    return endpoint;
  }

  const normalizedEndpoint = endpoint.startsWith("/")
    ? endpoint
    : `/${endpoint}`;

  return `${config.SERVELESS_API_URL}${normalizedEndpoint}`;
};

const getErrorMessage = (payload: unknown, fallbackMessage: string) => {
  if (
    payload &&
    typeof payload === "object" &&
    "message" in payload &&
    typeof payload.message === "string" &&
    payload.message.trim()
  ) {
    return payload.message;
  }

  return fallbackMessage;
};

const postJBARequest = async <T extends JBAApiResponseBase>(
  url: string,
  body: unknown,
  fallbackMessage: string
) => {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const data = (await response.json().catch(() => null)) as T | null;

  if (!response.ok || data?.success === false) {
    throw new Error(getErrorMessage(data, fallbackMessage));
  }

  return data;
};

export const getJBAFulfillmentUrl = () =>
  jbaConfig.fulfillmentEndpoint
    ? getJBAEndpointUrl(jbaConfig.fulfillmentEndpoint)
    : "";

export const submitJBAFreePdfLead = async (payload: JBAFreePdfLeadRequest) => {
  window.fbq?.("track", "Lead");
  return postJBARequest<JBAFreePdfLeadResponse>(
    getJBAEndpointUrl(FREE_PDF_ENDPOINT),
    payload,
    "Unable to send the free PDF right now."
  );
};

export const fulfillJBAPayment = async (payload: JBAFulfillmentRequest) => {
  const fulfillmentUrl = `${config.SERVELESS_API_URL}/jba/payment/fulfill`;

  return postJBARequest<JBAFulfillmentResponse>(
    fulfillmentUrl,
    payload,
    "Payment was received, but automatic delivery could not be completed."
  );
};
